import { type CSSProperties, type RefObject } from 'react';
import { tv } from 'tailwind-variants';

import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import './WizardStepCard.css';
import {
  CORNER_POINTER_CLASS,
  CORNER_POINTER_HEIGHT,
  CORNER_POINTER_WIDTH,
  getCornerPointerPath,
  getCornerPointerStyleVars,
  getCornerPointerSvgTransform,
  getCornerPointerVariant,
} from './cornerPointer';
import {
  WIZARD_STEP_CARD_ATTRIBUTE,
  WIZARD_STEP_CARD_POINTER_CARD_OVERLAP_PX,
  WIZARD_STEP_CARD_POINTER_HEIGHT,
  WIZARD_STEP_CARD_POINTER_WIDTH,
  getWizardStepCardPointerPath,
} from './constants';
import type { WizardPlacement, WizardPointerAnchor } from './types';
import { getWizardStepActions } from './wizardStepActions';

const pointerPlacementClassName: Record<WizardPlacement, string> = {
  top: 'wizard-step-card-pointer--top',
  bottom: 'wizard-step-card-pointer--bottom',
  left: 'wizard-step-card-pointer--left',
  right: 'wizard-step-card-pointer--right',
};

function getWizardStepCardPointerPathTransform(
  placement: WizardPlacement,
  width: number,
  height: number,
): string | undefined {
  const centerX = width / 2;
  const centerY = height / 2;

  switch (placement) {
    case 'bottom':
      return `rotate(180 ${centerX} ${centerY})`;
    case 'left':
      return `rotate(-90 ${centerX} ${centerY})`;
    case 'right':
      return `rotate(90 ${centerX} ${centerY})`;
    case 'top':
    default:
      return undefined;
  }
}

function WizardStepCardPointer({
  placement,
  pointerAnchor,
}: {
  placement: WizardPlacement;
  pointerAnchor?: WizardPointerAnchor;
}) {
  const cornerVariant = getCornerPointerVariant(placement, pointerAnchor);
  const pathWidth = cornerVariant ? CORNER_POINTER_WIDTH : WIZARD_STEP_CARD_POINTER_WIDTH;
  const pathHeight = cornerVariant ? CORNER_POINTER_HEIGHT : WIZARD_STEP_CARD_POINTER_HEIGHT;
  const pathTransform = cornerVariant
    ? getCornerPointerSvgTransform(cornerVariant, pathWidth, pathHeight)
    : getWizardStepCardPointerPathTransform(placement, pathWidth, pathHeight);
  const path = (
    <path
      d={
        cornerVariant
          ? getCornerPointerPath(pathWidth, pathHeight)
          : getWizardStepCardPointerPath(pathWidth, pathHeight)
      }
      fill="currentColor"
    />
  );

  const placementClassName = cornerVariant
    ? CORNER_POINTER_CLASS[cornerVariant]
    : pointerPlacementClassName[placement];

  return (
    <svg
      width={pathWidth}
      height={pathHeight}
      viewBox={`0 0 ${pathWidth} ${pathHeight}`}
      className={`wizard-step-card-pointer absolute overflow-visible text-white ${placementClassName}`}
      style={
        {
          '--wizard-step-card-pointer-overlap': `${WIZARD_STEP_CARD_POINTER_CARD_OVERLAP_PX}px`,
          ...(cornerVariant ? getCornerPointerStyleVars() : {}),
        } as CSSProperties
      }
      aria-hidden
    >
      {pathTransform ? <g transform={pathTransform}>{path}</g> : path}
    </svg>
  );
}

const wizardStepCard = tv({
  base: 'wizard-step-card shadow-menu fixed w-[min(100vw-2rem,300px)] rounded-3xl bg-white dark:bg-white',
  variants: {
    placement: {
      top: 'wizard-step-card--top',
      bottom: 'wizard-step-card--bottom',
      left: 'wizard-step-card--left',
      right: 'wizard-step-card--right',
    },
    revealed: {
      true: 'wizard-step-card--revealed pointer-events-auto opacity-100',
      false: 'pointer-events-none opacity-0',
    },
    pointerAnchor: {
      'top-left': 'wizard-step-card--pointer-top-left rounded-tl-none',
      'top-right': 'wizard-step-card--pointer-top-right rounded-tr-none',
      'bottom-right': 'wizard-step-card--pointer-bottom-right rounded-br-none',
      none: '',
    },
  },
  defaultVariants: {
    placement: 'top',
    revealed: false,
    pointerAnchor: 'none',
  },
});

type WizardStepCardProps = {
  title: string;
  body: string;
  stepIndex: number;
  totalSteps: number;
  placement: WizardPlacement;
  pointerAnchor?: WizardPointerAnchor;
  style: {
    top: number;
    left: number;
  };
  cardRef: RefObject<HTMLDivElement | null>;
  revealed: boolean;
  onBack: () => void;
  onDone: () => void;
  onEndTour: () => void;
  onNext: () => void;
};

export default function WizardStepCard({
  title,
  body,
  stepIndex,
  totalSteps,
  placement,
  pointerAnchor,
  style,
  cardRef,
  revealed,
  onBack,
  onDone,
  onEndTour,
  onNext,
}: WizardStepCardProps) {
  const actions = getWizardStepActions(stepIndex, totalSteps);
  const showProgress = totalSteps > 1;

  return (
    <div
      ref={cardRef}
      {...{ [WIZARD_STEP_CARD_ATTRIBUTE]: '' }}
      className={wizardStepCard({
        placement,
        revealed,
        pointerAnchor: pointerAnchor ?? 'none',
      })}
      role="dialog"
      aria-hidden={!revealed}
      aria-labelledby="wizard-step-title"
      aria-describedby="wizard-step-body"
      style={{ ...style, zIndex: 10002 }}
    >
      <WizardStepCardPointer
        placement={placement}
        pointerAnchor={pointerAnchor}
      />
      <div className="p-4">
        {showProgress ? (
          <Text as="p" token="14/medium" className="text-tertiary mb-3 mt-1">
            {stepIndex + 1} / {totalSteps}
          </Text>
        ) : null}
        <div className="mb-4 flex flex-col gap-1">
          <Text
            as="p"
            id="wizard-step-title"
            token="display/20/medium"
            leading="title"
            className="text-primary block pb-4 pt-1"
          >
            {title}
          </Text>
          <Text
            as="p"
            id="wizard-step-body"
            token="14"
            className="text-primary block pb-3"
          >
            {body}
          </Text>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div>
            {actions.endTour ? (
              <Button variant="secondary" size="sm" onClick={onEndTour}>
                End tour
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {actions.back ? (
              <Button variant="secondary" size="sm" onClick={onBack}>
                Back
              </Button>
            ) : null}
            {actions.next ? (
              <Button variant="primary" size="sm" onClick={onNext}>
                Next
              </Button>
            ) : null}
            {actions.done ? (
              <Button variant="primary" size="sm" onClick={onDone}>
                Done
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
